import { Sparkles } from "lucide-react";
import { extractDetailNames, type IntentSuggestion } from "../lib/useSearchIntent";

interface SearchSuggestionProps {
  suggestion: IntentSuggestion;
  isHighlighted: boolean;
  onSelect: () => void;
}

const DETAIL_NAME_LIMIT = 5;

// Renders the top-of-list "Suggested" row in the global search dropdown. One
// row, one suggestion — keeps the LLM contribution unambiguous and doesn't
// compete with the heuristic Quick Actions / record results below. Handles
// both action ("Log Incident for Bob Smith") and question ("3 children absent
// today") shapes via the discriminator on `suggestion.kind`.
export function SearchSuggestion({ suggestion, isHighlighted, onSelect }: SearchSuggestionProps) {
  const { label, subtitle } = formatSuggestion(suggestion);
  // Only question suggestions surface item-level names; action suggestions
  // already point at a single resolved record so no detail line is needed.
  const detailLine =
    suggestion.kind === "question" ? formatDetailLine(extractDetailNames(suggestion.data)) : null;

  return (
    <div>
      <div className="px-4 pt-2 pb-1 text-[10px] font-semibold uppercase tracking-wider text-muted">
        Suggested
      </div>
      <button
        data-search-item
        data-suggestion
        onClick={onSelect}
        className={`flex w-full items-center gap-3 px-4 py-2.5 text-left transition-colors ${
          isHighlighted ? "bg-surface2" : "hover:bg-surface2/60"
        }`}
      >
        <span className="shrink-0 rounded bg-accent/15 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-accent">
          <Sparkles size={10} className="inline -mt-0.5 mr-0.5" />
          Smart
        </span>
        <div className="min-w-0 flex-1">
          <div className="truncate text-sm font-medium text-foreground">{label}</div>
          <div className="truncate text-xs text-muted">{subtitle}</div>
          {detailLine && (
            <div data-suggestion-detail className="truncate text-xs text-muted">
              {detailLine}
            </div>
          )}
        </div>
      </button>
    </div>
  );
}

function formatDetailLine(names: string[] | null): string | null {
  if (!names) return null;
  if (names.length <= DETAIL_NAME_LIMIT) return names.join(", ");
  const shown = names.slice(0, DETAIL_NAME_LIMIT).join(", ");
  const remaining = names.length - DETAIL_NAME_LIMIT;
  return `${shown}, and ${remaining} more`;
}

function formatSuggestion(s: IntentSuggestion): { label: string; subtitle: string } {
  if (s.kind === "question") {
    // The summary is the headline answer ("3 children absent today"). The
    // source query echoes what the user typed so they can tell the answer
    // matched their question.
    return { label: s.summary, subtitle: `Question · ${s.source_query}` };
  }

  // Action variant — prefer the server-resolved record name over the raw
  // target_name from the LLM. The resolved name is the canonical DB display
  // string; raw target_name is only used when resolution found no match.
  const resolvedName = s.target_record?.display_name ?? null;
  const rawName = typeof s.prefill?.target_name === "string" ? (s.prefill.target_name as string) : null;
  const targetName = resolvedName ?? rawName;
  return {
    label: s.label,
    subtitle: targetName ? `${s.subtitle} — ${targetName}` : s.subtitle,
  };
}
